<template>
    <div class="youtube-comments-page">
        <!-- Left Panel: Comments -->
        <div class="comments-panel">
            <div class="panel-header">
                <h2 class="panel-title">
                    <span class="material-icons youtube-icon">forum</span>
                    {{ $t("youtubeChannel.comments") }}
                    <span class="count-badge">{{ total }}</span>
                </h2>
                <div class="header-actions">
                    <a-select
                        v-model="filters.videoId"
                        :placeholder="$t('youtubeChannel.selectVideo')"
                        style="width: 200px"
                        size="small"
                        allowClear
                        showSearch
                        :filterOption="filterVideoOption"
                    >
                        <a-select-option value="">{{ $t("youtubeChannel.allVideos") }}</a-select-option>
                        <a-select-option v-for="video in videos" :key="video.videoId" :value="video.videoId">
                            {{ video.title | truncate(40) }}
                        </a-select-option>
                    </a-select>
                    <a-select v-model="filters.status" style="width: 120px" size="small">
                        <a-select-option value="">{{ $t("youtubeChannel.all") }}</a-select-option>
                        <a-select-option value="new">{{ $t("youtubeChannel.new") }}</a-select-option>
                        <a-select-option value="replied">{{ $t("youtubeChannel.replied") }}</a-select-option>
                        <a-select-option value="resolved">{{ $t("youtubeChannel.resolved") }}</a-select-option>
                    </a-select>
                    <a-input-search
                        v-model="filters.search"
                        :placeholder="$t('youtubeChannel.searchComments')"
                        style="width: 180px"
                        size="small"
                        @search="fetchComments"
                    />
                    <a-button size="small" @click="fetchComments" :loading="loading">
                        <a-icon type="reload" />
                    </a-button>
                </div>
            </div>

            <div class="comments-list" v-if="!loading">
                <div
                    class="comment-item"
                    v-for="comment in comments"
                    :key="comment._id"
                    :class="{ selected: selectedComment && selectedComment._id === comment._id }"
                    @click="selectComment(comment)"
                >
                    <img :src="comment.authorProfileImageUrl" class="author-avatar" v-if="comment.authorProfileImageUrl" />
                    <span class="material-icons avatar-placeholder" v-else>account_circle</span>
                    <div class="comment-main">
                        <div class="comment-top">
                            <span class="author-name">{{ comment.authorDisplayName }}</span>
                            <span class="comment-date">{{ formatDate(comment.publishedAt) }}</span>
                            <span class="status-dot" :class="comment.status" :title="getStatusLabel(comment.status)"></span>
                        </div>
                        <p class="comment-text" v-html="truncateText(comment.text, 120)"></p>
                        <div class="comment-bottom">
                            <span class="like-info"><a-icon type="like" /> {{ comment.likeCount || 0 }}</span>
                            <span class="video-link" @click.stop="goToVideo(comment)" v-if="comment.videoTitle">
                                <a-icon type="play-circle" /> {{ comment.videoTitle | truncate(25) }}
                            </span>
                        </div>
                    </div>
                    <div class="comment-actions-mini">
                        <a-dropdown :trigger="['click']" @click.stop>
                            <a-button size="small" type="link"><a-icon type="more" /></a-button>
                            <template #overlay>
                                <a-menu>
                                    <a-sub-menu key="status" :title="$t('youtubeChannel.status')">
                                        <a-menu-item key="new" @click="updateStatus(comment, 'new')">
                                            <span class="status-indicator new"></span> {{ $t("youtubeChannel.new") }}
                                        </a-menu-item>
                                        <a-menu-item key="replied" @click="updateStatus(comment, 'replied')">
                                            <span class="status-indicator replied"></span> {{ $t("youtubeChannel.replied") }}
                                        </a-menu-item>
                                        <a-menu-item key="resolved" @click="updateStatus(comment, 'resolved')">
                                            <span class="status-indicator resolved"></span> {{ $t("youtubeChannel.resolved") }}
                                        </a-menu-item>
                                    </a-sub-menu>
                                    <a-sub-menu key="moderation" :title="$t('youtubeChannel.moderation')">
                                        <a-menu-item key="published" @click="handleModeration(comment, 'published')">
                                            <a-icon type="check-circle" style="color: #52c41a" /> {{ $t("youtubeChannel.publish") }}
                                        </a-menu-item>
                                        <a-menu-item key="heldForReview" @click="handleModeration(comment, 'heldForReview')">
                                            <a-icon type="clock-circle" style="color: #faad14" /> {{ $t("youtubeChannel.holdForReview") }}
                                        </a-menu-item>
                                        <a-menu-item key="rejected" @click="handleModeration(comment, 'rejected')">
                                            <a-icon type="close-circle" style="color: #ff4d4f" /> {{ $t("youtubeChannel.reject") }}
                                        </a-menu-item>
                                    </a-sub-menu>
                                    <a-menu-divider />
                                    <a-menu-item key="reply" @click="openReplyModal(comment)">
                                        <a-icon type="message" /> {{ $t("youtubeChannel.reply") }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </div>

                <div class="empty-state" v-if="comments.length === 0">
                    <span class="material-icons">chat_bubble_outline</span>
                    <p>{{ $t("youtubeChannel.noCommentsFound") }}</p>
                </div>
            </div>

            <div class="loading-state" v-else>
                <a-spin />
            </div>

            <div class="pagination-wrapper" v-if="total > pagination.limit">
                <a-pagination
                    :current="pagination.page"
                    :total="total"
                    :page-size="pagination.limit"
                    size="small"
                    @change="onPageChange"
                    @showSizeChange="onPageSizeChange"
                    show-size-changer
                    :page-size-options="['20', '50', '100']"
                />
            </div>
        </div>

        <!-- Right Panel: Video Preview -->
        <div class="video-panel" v-if="selectedVideo">
            <div class="video-header">
                <h3>{{ selectedVideo.title }}</h3>
                <a-button type="link" size="small" @click="goToVideoDetail">
                    {{ $t("youtubeChannel.viewDetails") }} <a-icon type="arrow-right" />
                </a-button>
            </div>
            <div class="video-embed">
                <iframe
                    :src="`https://www.youtube.com/embed/${selectedVideo.videoId}?rel=0&modestbranding=1`"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            </div>
            <div class="video-stats">
                <div class="stat">
                    <span class="material-icons">visibility</span>
                    <span>{{ formatNumber(selectedVideo.viewCount) }}</span>
                </div>
                <div class="stat">
                    <span class="material-icons">thumb_up</span>
                    <span>{{ formatNumber(selectedVideo.likeCount) }}</span>
                </div>
                <div class="stat">
                    <span class="material-icons">chat_bubble</span>
                    <span>{{ formatNumber(selectedVideo.commentCount) }}</span>
                </div>
            </div>

            <!-- Selected Comment Detail -->
            <div class="selected-comment-detail" v-if="selectedComment">
                <div class="detail-header">
                    <img :src="selectedComment.authorProfileImageUrl" v-if="selectedComment.authorProfileImageUrl" />
                    <span class="material-icons" v-else>account_circle</span>
                    <div class="author-details">
                        <span class="name">{{ selectedComment.authorDisplayName }}</span>
                        <span class="date">{{ formatDate(selectedComment.publishedAt) }}</span>
                    </div>
                    <div class="badges">
                        <span class="status-badge" :class="selectedComment.status">{{ getStatusLabel(selectedComment.status) }}</span>
                        <span class="moderation-badge" :class="selectedComment.moderationStatus" v-if="selectedComment.moderationStatus">
                            {{ getModerationLabel(selectedComment.moderationStatus) }}
                        </span>
                    </div>
                </div>
                <div class="detail-body" v-html="selectedComment.text"></div>
                <div class="detail-actions">
                    <a-button size="small" type="primary" @click="openReplyModal(selectedComment)">
                        <a-icon type="message" /> {{ $t("youtubeChannel.reply") }}
                    </a-button>
                    <a-dropdown :trigger="['click']">
                        <a-button size="small">{{ $t("youtubeChannel.status") }} <a-icon type="down" /></a-button>
                        <template #overlay>
                            <a-menu @click="(e) => updateStatus(selectedComment, e.key)">
                                <a-menu-item key="new">{{ $t("youtubeChannel.new") }}</a-menu-item>
                                <a-menu-item key="replied">{{ $t("youtubeChannel.replied") }}</a-menu-item>
                                <a-menu-item key="resolved">{{ $t("youtubeChannel.resolved") }}</a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                    <a-dropdown :trigger="['click']">
                        <a-button size="small">{{ $t("youtubeChannel.moderation") }} <a-icon type="down" /></a-button>
                        <template #overlay>
                            <a-menu @click="(e) => handleModeration(selectedComment, e.key)">
                                <a-menu-item key="published">
                                    <a-icon type="check-circle" style="color: #52c41a" /> {{ $t("youtubeChannel.publish") }}
                                </a-menu-item>
                                <a-menu-item key="heldForReview">
                                    <a-icon type="clock-circle" style="color: #faad14" /> {{ $t("youtubeChannel.holdForReview") }}
                                </a-menu-item>
                                <a-menu-item key="rejected">
                                    <a-icon type="close-circle" style="color: #ff4d4f" /> {{ $t("youtubeChannel.reject") }}
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </div>
            </div>
        </div>

        <!-- Empty Video Panel -->
        <div class="video-panel empty" v-else>
            <span class="material-icons">smart_display</span>
            <p>{{ $t("youtubeChannel.selectCommentToPreview") }}</p>
        </div>

        <!-- Reply Modal -->
        <a-modal
            v-model="replyModal.visible"
            :title="$t('youtubeChannel.replyToComment')"
            @ok="submitReply"
            :confirm-loading="replyModal.loading"
            width="500px"
        >
            <div class="reply-preview" v-if="replyModal.comment">
                <img :src="replyModal.comment.authorProfileImageUrl" v-if="replyModal.comment.authorProfileImageUrl" />
                <div>
                    <strong>{{ replyModal.comment.authorDisplayName }}</strong>
                    <p v-html="truncateText(replyModal.comment.text, 200)"></p>
                </div>
            </div>
            <a-textarea
                v-model="replyModal.text"
                :placeholder="$t('youtubeChannel.writeYourReply')"
                :rows="4"
                :maxLength="10000"
            />
        </a-modal>
    </div>
</template>

<script>
import { YOUTUBE_API } from "../../../../../api/youtube";
import moment from "moment";

export default {
    name: "CommentList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    filters: {
        truncate(value, length) {
            if (!value) return "";
            if (value.length <= length) return value;
            return value.substring(0, length) + "...";
        },
    },
    data() {
        return {
            comments: [],
            videos: [],
            loading: false,
            total: 0,
            selectedComment: null,
            selectedVideo: null,
            filters: {
                status: "",
                search: "",
                videoId: "",
            },
            pagination: {
                page: 1,
                limit: 20,
            },
            replyModal: {
                visible: false,
                comment: null,
                text: "",
                loading: false,
            },
        };
    },
    methods: {
        getStatusLabel(status) {
            const labels = {
                new: this.$t("youtubeChannel.new"),
                replied: this.$t("youtubeChannel.replied"),
                resolved: this.$t("youtubeChannel.resolved"),
            };
            return labels[status] || status;
        },
        getModerationLabel(status) {
            const labels = {
                published: this.$t("youtubeChannel.published"),
                heldForReview: this.$t("youtubeChannel.heldForReview"),
                rejected: this.$t("youtubeChannel.rejected"),
            };
            return labels[status] || status;
        },
        async fetchVideos() {
            try {
                const response = await YOUTUBE_API.listVideos(this.channel._id, 1, 100);
                this.videos = response.data.videos || [];
            } catch (err) {
                console.error("Error fetching videos", err);
            }
        },
        async fetchComments() {
            this.loading = true;
            try {
                const response = await YOUTUBE_API.listComments({
                    channelId: this.channel._id,
                    videoId: this.filters.videoId || undefined,
                    status: this.filters.status || undefined,
                    search: this.filters.search || undefined,
                    parentId: null,
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                });
                this.comments = response.data.comments;
                this.total = response.data.total;
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorFetchingComments"));
            } finally {
                this.loading = false;
            }
        },
        selectComment(comment) {
            this.selectedComment = comment;
            const video = this.videos.find(function(v) { return v.videoId === comment.videoId; });
            if (video) {
                this.selectedVideo = video;
            }
        },
        async updateStatus(comment, status) {
            try {
                await YOUTUBE_API.updateStatus(comment._id, status);
                // Local state güncelle
                comment.status = status;
                if (this.selectedComment && this.selectedComment._id === comment._id) {
                    this.selectedComment.status = status;
                }
                this.$message.success(this.$t("youtubeChannel.statusUpdated"));
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorUpdatingStatus"));
            }
        },
        async handleModeration(comment, action) {
            let moderationStatus = action;
            let banAuthor = false;

            if (action === "rejected-ban") {
                moderationStatus = "rejected";
                banAuthor = true;
            }

            try {
                await YOUTUBE_API.setModerationStatus(comment._id, this.channel._id, moderationStatus, banAuthor);
                // Local state güncelle
                comment.moderationStatus = moderationStatus;
                if (this.selectedComment && this.selectedComment._id === comment._id) {
                    this.selectedComment.moderationStatus = moderationStatus;
                }
                this.$message.success(this.$t("youtubeChannel.moderationSuccess"));
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorModeration"));
            }
        },
        openReplyModal(comment) {
            this.replyModal.comment = comment;
            this.replyModal.text = "";
            this.replyModal.visible = true;
        },
        async submitReply() {
            if (!this.replyModal.text.trim()) {
                this.$message.warning(this.$t("youtubeChannel.pleaseEnterReply"));
                return;
            }
            this.replyModal.loading = true;
            try {
                await YOUTUBE_API.replyComment(this.replyModal.comment._id, {
                    text: this.replyModal.text,
                    channelId: this.channel._id,
                });
                // Local state güncelle
                this.replyModal.comment.status = "replied";
                if (this.selectedComment && this.selectedComment._id === this.replyModal.comment._id) {
                    this.selectedComment.status = "replied";
                }
                this.$message.success(this.$t("youtubeChannel.replySuccess"));
                this.replyModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorSendingReply"));
            } finally {
                this.replyModal.loading = false;
            }
        },
        goToVideo(comment) {
            this.$router.push("/integrations/youtube-comments/" + this.channel._id + "/video/" + comment.videoId);
        },
        goToVideoDetail() {
            if (this.selectedVideo) {
                this.$router.push("/integrations/youtube-comments/" + this.channel._id + "/video/" + this.selectedVideo.videoId);
            }
        },
        onPageChange(page) {
            this.pagination.page = page;
            this.fetchComments();
        },
        onPageSizeChange(current, size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.fetchComments();
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        formatNumber(num) {
            if (!num) return "0";
            var n = parseInt(num);
            if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
            if (n >= 1000) return (n / 1000).toFixed(1) + "K";
            return n.toString();
        },
        truncateText(text, length) {
            if (!text) return "";
            var plainText = text.replace(/<[^>]*>/g, "");
            if (plainText.length <= length) return text;
            return plainText.substring(0, length) + "...";
        },
        filterVideoOption(input, option) {
            var title = option.componentOptions.children[0].text || "";
            return title.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
    },
    watch: {
        "filters.status"() {
            this.pagination.page = 1;
            this.fetchComments();
        },
        "filters.videoId"() {
            this.pagination.page = 1;
            this.fetchComments();
        },
    },
    mounted() {
        this.fetchVideos();
        this.fetchComments();
    },
};
</script>

<style lang="scss" scoped>
.youtube-comments-page {
    display: flex;
    gap: 20px;
    height: calc(100vh - 200px);
    padding: 16px;

    .comments-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;

        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border-bottom: 1px solid var(--color-border-default);
            flex-wrap: wrap;
            gap: 12px;

            .panel-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 600;
                margin: 0;

                .youtube-icon {
                    color: #ff0000;
                    font-size: 20px;
                }

                .count-badge {
                    background: var(--color-bg-subtle);
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
            }

            .header-actions {
                display: flex;
                gap: 8px;
                align-items: center;
            }
        }

        .comments-list {
            flex: 1;
            overflow-y: auto;
            padding: 8px;

            .comment-item {
                display: flex;
                gap: 12px;
                padding: 12px;
                border-radius: 8px;
                cursor: pointer;
                transition: background 0.15s;
                border: 1px solid transparent;

                &:hover {
                    background: var(--color-bg-subtle);
                }

                &.selected {
                    background: var(--color-bg-subtle);
                    border-color: var(--color-border-strong);
                }

                .author-avatar {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                .avatar-placeholder {
                    font-size: 36px;
                    color: var(--color-text-default);
                    flex-shrink: 0;
                }

                .comment-main {
                    flex: 1;
                    min-width: 0;

                    .comment-top {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 4px;

                        .author-name {
                            font-weight: 600;
                            font-size: 13px;
                        }

                        .comment-date {
                            font-size: 11px;
                            color: var(--color-text-default);
                        }

                        .status-dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            margin-left: auto;

                            &.new { background: #1890ff; }
                            &.replied { background: #52c41a; }
                            &.resolved { background: #8c8c8c; }
                        }
                    }

                    .comment-text {
                        font-size: 13px;
                        line-height: 1.5;
                        margin: 0 0 6px;
                        color: var(--color-text-strong-default);
                    }

                    .comment-bottom {
                        display: flex;
                        gap: 12px;
                        font-size: 11px;
                        color: var(--color-text-default);

                        .like-info, .video-link {
                            display: flex;
                            align-items: center;
                            gap: 4px;
                        }

                        .video-link {
                            cursor: pointer;
                            &:hover { color: #ff0000; }
                        }
                    }
                }

                .comment-actions-mini {
                    opacity: 0;
                    transition: opacity 0.15s;
                }

                &:hover .comment-actions-mini {
                    opacity: 1;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 60px 20px;
                color: var(--color-text-default);

                .material-icons {
                    font-size: 48px;
                    margin-bottom: 12px;
                    opacity: 0.5;
                }
            }
        }

        .loading-state {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
        }

        .pagination-wrapper {
            padding: 12px 16px;
            border-top: 1px solid var(--color-border-default);
            display: flex;
            justify-content: center;
        }
    }

    .video-panel {
        width: 380px;
        flex-shrink: 0;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        display: flex;
        flex-direction: column;
        overflow: hidden;

        &.empty {
            justify-content: center;
            align-items: center;
            color: var(--color-text-default);

            .material-icons {
                font-size: 48px;
                margin-bottom: 12px;
                opacity: 0.4;
            }

            p {
                font-size: 14px;
                margin: 0;
            }
        }

        .video-header {
            padding: 12px 16px;
            border-bottom: 1px solid var(--color-border-default);
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 8px;

            h3 {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                line-height: 1.4;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
        }

        .video-embed {
            position: relative;
            padding-bottom: 56.25%;
            background: #000;

            iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }
        }

        .video-stats {
            display: flex;
            justify-content: space-around;
            padding: 12px;
            border-bottom: 1px solid var(--color-border-default);

            .stat {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 13px;

                .material-icons {
                    font-size: 18px;
                    color: var(--color-text-default);
                }
            }
        }

        .selected-comment-detail {
            flex: 1;
            overflow-y: auto;
            padding: 16px;

            .detail-header {
                display: flex;
                gap: 10px;
                margin-bottom: 12px;

                img {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                }

                .material-icons {
                    font-size: 40px;
                    color: var(--color-text-default);
                }

                .author-details {
                    flex: 1;

                    .name {
                        display: block;
                        font-weight: 600;
                        font-size: 14px;
                    }

                    .date {
                        font-size: 12px;
                        color: var(--color-text-default);
                    }
                }

                .badges {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;

                    .status-badge, .moderation-badge {
                        padding: 2px 8px;
                        border-radius: 10px;
                        font-size: 10px;
                        text-align: center;
                    }

                    .status-badge {
                        &.new { background: #e6f7ff; color: #1890ff; }
                        &.replied { background: #f6ffed; color: #52c41a; }
                        &.resolved { background: #f5f5f5; color: #8c8c8c; }
                    }

                    .moderation-badge {
                        &.published { background: #f6ffed; color: #52c41a; }
                        &.heldForReview { background: #fff7e6; color: #fa8c16; }
                        &.rejected { background: #fff1f0; color: #f5222d; }
                    }
                }
            }

            .detail-body {
                font-size: 14px;
                line-height: 1.6;
                margin-bottom: 16px;
                padding: 12px;
                background: var(--color-bg-subtle);
                border-radius: 8px;
            }

            .detail-actions {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }
        }
    }

    .reply-preview {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: var(--color-bg-subtle);
        border-radius: 8px;
        margin-bottom: 16px;

        img {
            width: 32px;
            height: 32px;
            border-radius: 50%;
        }

        p {
            margin: 4px 0 0;
            font-size: 13px;
            color: var(--color-text-default);
        }
    }

    .status-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;

        &.new { background: #1890ff; }
        &.replied { background: #52c41a; }
        &.resolved { background: #8c8c8c; }
    }
}
</style>
